import React, { useState, useEffect } from "react";
import { noShowDetails, noShowDelete } from "../../../../Api/apiUtils";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Trash } from "react-feather";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button, Card, Table, Field, Toolbar, Loader, EmptyState, DescriptionList } from "../../../../ui";
import "./noShowDetails.scss";

interface StudentData {
  studentId: string;
  studentName: string;
  courseName: string;
  cancelCount: any;
  slotDate: any;
  startTime: any;
  teacherNoshowClassId: string;
  teacherId: any;
}

interface NoShowDetailsProps {
  teacherId: string;
}

const NoShowDetails: React.FC<NoShowDetailsProps> = ({ teacherId }) => {
  const [studentData, setStudentData] = useState<StudentData[]>([]);
  const [filteredData, setFilteredData] = useState<StudentData[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  useEffect(() => {
    fetchNoShowDetails();
  }, [teacherId]);

  const fetchNoShowDetails = async (from?: string, to?: string) => {
    setLoading(true);
    try {
      const response = await noShowDetails(teacherId, from, to);
      if (!response || response.length === 0) {
        toast.info("No no-show data available.");
        setStudentData([]);
        setFilteredData([]);
      } else {
        setStudentData(response);
        setFilteredData(response);
      }
    } catch (error) {
      toast.error("Error fetching no-show data.");
      console.error("Error fetching no-show data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (selectedStudent) {
      try {
        await noShowDelete(selectedStudent.teacherId, selectedStudent.teacherNoshowClassId);
        toast.success("No-show class deleted successfully.");
        setStudentData((prevData) =>
          prevData.filter(
            (student) => student.teacherNoshowClassId !== selectedStudent.teacherNoshowClassId
          )
        );
        setFilteredData((prevData) =>
          prevData.filter(
            (student) => student.teacherNoshowClassId !== selectedStudent.teacherNoshowClassId
          )
        );
      } catch (error) {
        toast.error("Error deleting no-show class.");
        console.error("Error deleting no-show class:", error);
      }
    }
    setDeleteModalOpen(false);
  };

  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateFilter = () => {
    if (fromDate && toDate) {
      fetchNoShowDetails(formatLocalDate(fromDate), formatLocalDate(toDate));
    } else {
      toast.warn("Please select both from and to dates");
    }
  };

  const handleResetDates = () => {
    setFromDate(null);
    setToDate(null);
    fetchNoShowDetails();
  };

  const classDateTime = (student: StudentData) =>
    `${student.slotDate || ""} ${student.startTime ? `& ${student.startTime}` : ""}`.trim();

  const openDelete = (student: StudentData) => {
    setSelectedStudent(student);
    setDeleteModalOpen(true);
  };

  return (
    <div className="tp-noshow">
      <Card title="No Show Details" flush>
        <Toolbar className="tp-noshow__filter">
          <Field label="From Date">
            {(control) => (
              <DatePicker
                id={control.id}
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                className="ds-input"
                placeholderText="Select start date"
                wrapperClassName="tp-noshow__datepicker"
                popperPlacement="bottom-start"
              />
            )}
          </Field>
          <Field label="To Date">
            {(control) => (
              <DatePicker
                id={control.id}
                selected={toDate}
                onChange={(date) => setToDate(date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate || undefined}
                className="ds-input"
                placeholderText="Select end date"
                wrapperClassName="tp-noshow__datepicker"
                popperPlacement="bottom-start"
              />
            )}
          </Field>
          <div className="tp-noshow__actions">
            <Button variant="primary" onClick={handleDateFilter}>
              Apply
            </Button>
            <Button variant="secondary" onClick={handleResetDates}>
              Reset
            </Button>
          </div>
        </Toolbar>

        {/* Desktop / tablet table */}
        <div className="tp-noshow__desktop">
          <Table caption="No-show classes" className="tp-noshow__table">
            <thead>
              <tr>
                <th scope="col">Student ID</th>
                <th scope="col">Student Name</th>
                <th scope="col">Course Name</th>
                <th scope="col">Class Number</th>
                <th scope="col">Class Date &amp; Time</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>
                    <Loader />
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="No no-show data available" description="Try a different date range or reset the filter." />
                  </td>
                </tr>
              ) : (
                filteredData.map((student) => (
                  <tr key={`${student.studentId}-${student.teacherNoshowClassId}`}>
                    <td className="ds-num">{student.studentId}</td>
                    <td>{student.studentName}</td>
                    <td>{student.courseName}</td>
                    <td className="ds-num">{student.cancelCount}</td>
                    <td>{classDateTime(student)}</td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="tp-noshow__remove"
                        icon={<Trash size={16} aria-hidden />}
                        aria-label={`Delete no-show class for ${student.studentName}`}
                        onClick={() => openDelete(student)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="tp-noshow__mobile">
          {loading ? (
            <Loader />
          ) : filteredData.length === 0 ? (
            <EmptyState title="No no-show data available" description="Try a different date range or reset the filter." />
          ) : (
            filteredData.map((student) => (
              <div
                key={`${student.studentId}-${student.teacherNoshowClassId}`}
                className="tp-noshow__card"
              >
                <DescriptionList
                  items={[
                    { label: "Student", value: `${student.studentName} (${student.studentId})` },
                    { label: "Course", value: student.courseName },
                    { label: "Class #", value: student.cancelCount },
                    { label: "Date & Time", value: classDateTime(student) },
                  ]}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  block
                  className="tp-noshow__remove"
                  icon={<Trash size={16} aria-hidden />}
                  onClick={() => openDelete(student)}
                >
                  Delete
                </Button>
              </div>
            ))
          )}
        </div>
      </Card>

      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} centered>
        <ModalHeader toggle={() => setDeleteModalOpen(false)}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete the no-show class for {selectedStudent?.studentName}?
        </ModalBody>
        <ModalFooter className="tp-noshow__modal-actions">
          <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default NoShowDetails;
